/**
 * SettingsView (Figma 09 · Settings and connectors). The desk shows the
 * scoped nav beside the page; the compact shell shows All settings, then one
 * page with a way back. A guest gets the same layout with only their pages
 * (Your access first).
 * ?page= keeps every old id working (settingsNav LEGACY_PAGE_MAP): the social
 * OAuth callbacks still land on ?page=connections.
 */
import { useEffect } from "react";
import { Link, useSearchParams } from "react-router";
import { ChevronLeft } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import {
  canOpenPage,
  defaultPageFor,
  resolveSettingsPage,
  type SettingsPageId,
} from "@/app/routes/dashboard/settings/settingsNav";
import SettingsNav from "../components/settings/SettingsNav";
import AllSettingsIndex from "../components/settings/AllSettingsIndex";
import { useSocialOAuthPopupReturn } from "../components/settings/socialOAuthPopup";
import ProfilePage from "../components/settings/pages/ProfilePage";
import WorkspacesPage from "../components/settings/pages/WorkspacesPage";
import PersonalMemoryPage from "../components/settings/pages/PersonalMemoryPage";
import AppearancePage from "../components/settings/pages/AppearancePage";
import YourUsagePage from "../components/settings/pages/YourUsagePage";
import ConnectedAppsPage from "../components/settings/pages/ConnectedAppsPage";
import GeneralPage from "../components/settings/pages/GeneralPage";
import PeoplePage from "../components/settings/pages/PeoplePage";
import AccountsPage from "../components/settings/pages/AccountsPage";
import BrandsPage from "../components/settings/pages/BrandsPage";
import WorkspaceUsagePage from "../components/settings/pages/WorkspaceUsagePage";
import PlanPage from "../components/settings/pages/PlanPage";
import SocialAccountsPage from "../components/settings/pages/SocialAccountsPage";
import ClientsPage from "../components/settings/pages/ClientsPage";
import BrandMemoryPage from "../components/settings/pages/BrandMemoryPage";
import CalendarSettingsPage from "../components/settings/pages/CalendarSettingsPage";
import YourAccessPage from "../components/settings/pages/YourAccessPage";

function SettingsContent({ page }: { page: SettingsPageId }) {
  switch (page) {
    case "profile":
      return <ProfilePage />;
    case "workspaces":
      return <WorkspacesPage />;
    case "personal-memory":
      return <PersonalMemoryPage />;
    case "appearance":
      return <AppearancePage />;
    case "your-usage":
      return <YourUsagePage />;
    case "connected-apps":
      return <ConnectedAppsPage />;
    case "general":
      return <GeneralPage />;
    case "people":
      return <PeoplePage />;
    case "accounts":
      return <AccountsPage />;
    case "brands":
      return <BrandsPage />;
    case "workspace-usage":
      return <WorkspaceUsagePage />;
    case "plan":
      return <PlanPage />;
    case "social-accounts":
      return <SocialAccountsPage />;
    case "clients":
      return <ClientsPage />;
    case "brand-memory":
      return <BrandMemoryPage />;
    case "calendar":
      return <CalendarSettingsPage />;
    case "your-access":
      return <YourAccessPage />;
  }
}

function BackLink({ to, children }: { to: string; children: string }) {
  return (
    <Link
      to={to}
      className="unstyled-link inline-flex h-8 items-center gap-1 self-start text-[13px] font-medium text-co-accent hover:text-co-accent-ink"
    >
      <ChevronLeft aria-hidden className="size-3.5" />
      {children}
    </Link>
  );
}

export default function SettingsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedOrganization, user } = useCurrentUser();
  const isCompact = useMediaQuery("(max-width: 1023px)");
  const hasOrg = (user?.userOrganizations?.length ?? 0) > 0;
  const role = hasOrg ? (selectedOrganization?.role ?? null) : null;

  // A popup that finished a social sign-in reports back and closes itself.
  const isOAuthPopup = useSocialOAuthPopupReturn();

  const pageParam = searchParams.get("page") ?? searchParams.get("tab");
  const requested = resolveSettingsPage(pageParam);
  // Wait for the role before bouncing anyone off a page.
  const roleKnown = !hasOrg || selectedOrganization != null;
  const blocked = requested != null && roleKnown && !canOpenPage(requested, role);

  let page: SettingsPageId | null = requested && !blocked ? requested : null;
  if (!page && !isCompact) page = defaultPageFor(role);

  useEffect(() => {
    if (blocked) setSearchParams({}, { replace: true });
  }, [blocked, setSearchParams]);

  if (isOAuthPopup) return null;

  if (isCompact) {
    if (!page) return <AllSettingsIndex />;
    return (
      <div className="mx-auto flex w-full max-w-[976px] flex-col gap-4">
        <BackLink to=".">All settings</BackLink>
        <SettingsContent page={page} />
      </div>
    );
  }

  const active = page ?? defaultPageFor(role);
  return (
    <div className="mx-auto flex w-full max-w-[1224px] items-start gap-10">
      <SettingsNav activePage={active} />
      <div className="min-w-0 max-w-[976px] flex-1">
        <SettingsContent key={active} page={active} />
      </div>
    </div>
  );
}
